import { useAppSelector } from 'app/hooks';
import { CheckoutTypes, PaymentMethodTypes } from '../../constants';
import {
  selectConfirmPayment,
  selectDataRequired,
  selectPaymentData,
  selectPaymentMethodSelected,
} from 'features/payment/payment-slice';
import { useEffect, useRef, useState } from 'react';
import { getErrorKeys, openNewTabAndFocus, redirecTo, sendPostToShopify } from 'utils';
import { useTranslation } from 'react-i18next';
import { Link } from 'components/common/link';
import styles from './styles.module.scss';
import { ErrorInfo } from 'utils/error-codes';
import CardResult from 'components/card-result';
import { useMediaQuery } from '@mui/material';

const ResultContent = () => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const paymentData = useAppSelector(selectPaymentData);
  const dataRequired = useAppSelector(selectDataRequired);
  const paymentMethodSelected = useAppSelector(selectPaymentMethodSelected);
  const confirmPayment = useAppSelector(selectConfirmPayment);
  const redirectTime = useRef(5);
  const [redirecting, setRedirecting] = useState(5);

  const handleRedirect = (url: string, isShopify?: boolean) => {
    const intervalId = setInterval(() => {
      if (redirectTime.current > 0) {
        redirectTime.current -= 1;
        setRedirecting(redirectTime.current);
      } else {
        clearInterval(intervalId);
        if (isShopify) {
          sendPostToShopify(url);
        } else {
          redirecTo(url);
        }
      }
    }, 1000);
  };

  useEffect(() => {
    const { success } = confirmPayment;
    if (success) {
      if (success?.successUrl) {
        handleRedirect(success.successUrl);
      } else if (success?.url && !success.iframe) {
        openNewTabAndFocus(success.url);
      } else if (success?.shopifySuccessUrl) {
        handleRedirect(success.shopifySuccessUrl, true);
      }
    }
  }, [confirmPayment.success]);

  const { loading, error, errorInfo, success, summaryData } = confirmPayment;
  const isCC = paymentMethodSelected?.type === PaymentMethodTypes.CREDIT_CARD;

  const getErrorMessage = (err: ErrorInfo | null) => {
    const { titleKey, messageKey } = getErrorKeys(err);
    return (
      <div>
        {titleKey && <div className={styles.result_title}>{t(titleKey)}</div>}
        {titleKey && messageKey && <div className={styles.result_separator} />}
        {messageKey && <div className={styles.result_title}>{t(messageKey)}</div>}
      </div>
    );
  };

  return (
    <CardResult error={error} backUrl={paymentData?.backUrl || undefined}>
      {!loading && error && getErrorMessage(errorInfo)}
      {!loading && !error && success && !success.iframe && (
        <>
          {((isCC && success.type !== CheckoutTypes.SHOPIFY) ||
            (isCC && success.type === CheckoutTypes.SHOPIFY && success.shopifySuccess)) && (
            <div className={styles.result_title}>
              {t('resultContent.success.card', {
                0:
                  paymentData?.paymentInfo.currency +
                  ' ' +
                  (paymentData?.paymentInfo?.total !== dataRequired?.instalmentSelected?.totalAmount
                    ? dataRequired?.instalmentSelected?.totalAmount
                    : paymentData?.paymentInfo.total),
              })}
            </div>
          )}
          {!isCC && (
            <div className={styles.result_title}>
              {t('resultContent.success.apm', {
                0: paymentData?.paymentInfo.currency + ' ' + paymentData?.paymentInfo.total,
              })}
            </div>
          )}
          <div className={styles.result_separator} />
          {isCC && (
            <div className={styles.result_desc}>
              <p>
                {summaryData?.bin && (
                  <span>
                    {t('resultContent.summaryData.card', { 0: summaryData?.bin })} {isDesktop ? <span>|</span> : <br />}
                  </span>
                )}
                <span> </span>
                {summaryData?.currentTime}
              </p>
              {success.externalId && <p>{t('resultContent.summaryData.reference', { 0: success.externalId })}</p>}
            </div>
          )}
          <div className={styles.result_text_completed}>
            {success?.url && (
              <div className={styles.result_text_completed_blocked_message}>
                <span>{t('resultContent.success.newWindow')} </span>
                <Link
                  onClick={() => {
                    if (success.url) openNewTabAndFocus(success.url);
                  }}
                >
                  {t('resultContent.success.newWindow.here')}
                </Link>
              </div>
            )}
          </div>
          {(success.successUrl || success.shopifySuccessUrl) && (
            <div className={styles.result_redirecting}>{t('resultContent.success.redirecting', { 0: redirecting })}</div>
          )}
        </>
      )}
    </CardResult>
  );
};

export default ResultContent;
